import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownToLine, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export function DowngradeCard({ corp }) {
  const nav = useNavigate();
  const [check, setCheck] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/corporate/downgrade-check").then((r) => setCheck(r.data)).catch(() => setCheck(null)); }, []);

  const downgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/corporate/downgrade", { confirm_name: confirm });
      toast.success(`${data.company_name} is now a standalone account`);
      window.location.assign("/admin");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Downgrade failed"); }
    finally { setBusy(false); }
  };

  if (!check) return null;
  const blocking = check.blocking_locations || [];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm" data-testid="corporate-downgrade-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0"><ArrowDownToLine className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg font-bold text-slate-900">Downgrade to a single location</h2>
          <p className="mt-1 text-sm text-slate-600">
            Dissolves <b>{corp?.name}</b> and turns <b>{check.primary?.name || "your primary location"}</b> back into a standalone account.
            Corporate admins become regular admins there, and any remaining paid or trial time carries over. District-wide files, buses, calendars and forms stop being shared.
          </p>
          {blocking.length > 0 ? (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="downgrade-blocked">
              <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" /> Delete these locations first — only the primary location can be kept:</div>
              <ul className="mt-1.5 list-disc pl-6 space-y-0.5">
                {blocking.map((l) => <li key={l.id}>{l.name}{l.archived ? " (archived)" : ""}</li>)}
              </ul>
              <button onClick={() => nav("/corporate/locations")} className="mt-2 text-xs font-semibold underline">Go to Locations</button>
            </div>
          ) : !open ? (
            <button onClick={() => setOpen(true)} data-testid="downgrade-start-btn"
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Ready to downgrade
            </button>
          ) : (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50/60 p-4 space-y-3" data-testid="downgrade-confirm">
              <div className="text-sm text-red-900">This can't be undone. Type <b>{corp?.name}</b> to confirm.</div>
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="downgrade-confirm-input" placeholder={corp?.name}
                className="w-full h-10 rounded-lg border border-red-200 bg-white px-3 text-sm focus:border-red-400 focus:ring-4 focus:ring-red-100 outline-none" />
              <div className="flex gap-2">
                <button onClick={downgrade} disabled={busy || confirm.trim().toLowerCase() !== (corp?.name || "").trim().toLowerCase()} data-testid="downgrade-submit-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Downgrade now
                </button>
                <button onClick={() => { setOpen(false); setConfirm(""); }} className="rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-white">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
